import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import React, { useCallback } from 'react';
import { HeatByPlace } from '../hooks';
import { CustomSuburb } from './util';

// A set of 10 colours in RGB format, from red to green, with orange in the middle
const heatColours = [
	'#F22',
	'#E33',
	'#D44',
	'#C65',
	'#D83',
	'#CA4',
	'#AA5',
	'#9B6',
	'#8C6',
	'#7D6',
];

/**
 * Returns a color between minColor and maxColor based on the value
 * @param value - The value to scale, from 0 to 1
 * @returns A color between minColor and maxColor
 */
const getScaledColor = (value: number) => {
	const index = Math.round(value * (heatColours.length - 1));
	return heatColours[index];
};

export const SuburbLayer: React.FC<{
	activeViewport: google.maps.LatLngBounds | null;
	councilPlaceId: string;
	suburbPlaceIds: string[];
	onSuburbSelected: (placeId: string) => void;
	selectedSuburbPlaceId: string | null | undefined;
	heatOfPlaces: HeatByPlace;
	customSuburbs: CustomSuburb[];
}> = ({
	activeViewport,
	councilPlaceId,
	suburbPlaceIds,
	onSuburbSelected,
	selectedSuburbPlaceId,
	heatOfPlaces,
	customSuburbs,
}) => {
	const map = useMap();
	const placesLib = useMapsLibrary('places');
	const oneFeatureIsSelected = Boolean(selectedSuburbPlaceId);

	const getStyleForSuburb = useCallback(
		(placeId: string) => {
			const featureIsInView = suburbPlaceIds.indexOf(placeId) !== -1;
			const thisFeatureIsSelected = placeId === selectedSuburbPlaceId;

			if (featureIsInView) {
				const heatValue = heatOfPlaces[placeId];
				const scaledValue = heatValue === undefined ? undefined : getScaledColor(heatValue);
				const heatColor = scaledValue ?? '#CCC';
				const borderColour = thisFeatureIsSelected ? '#3399af' : heatColor;
				const strokeWeight = thisFeatureIsSelected ? 5.0 : 2.0;

				return {
					strokeColor: borderColour,
					fillColor: heatColor,
					strokeOpacity: 1.0,
					strokeWeight,
					fillOpacity: 0.4,
				};
			}

			return {
				strokeColor: '#999',
				fillColor: '#999',
				strokeOpacity: 0.8,
				strokeWeight: 2.0,
				fillOpacity: 0.1,
			};
		},
		[suburbPlaceIds, selectedSuburbPlaceId, heatOfPlaces]
	);

	React.useEffect(() => {
		if (!map || !placesLib) return;

		// Use Google's native Feature Layer for Administrative Area Level 2 (Councils/LGAs)
		const councilLayer = map.getFeatureLayer(google.maps.FeatureType.ADMINISTRATIVE_AREA_LEVEL_2);
		const localityLayer = map.getFeatureLayer(google.maps.FeatureType.LOCALITY);

		const customSuburbsDataLayer = new google.maps.Data({ map });

		customSuburbs.forEach((customSuburb) => {
			customSuburbsDataLayer.addGeoJson({
				...customSuburb.boundaryFeature,
				properties: {
					suburbId: customSuburb.id,
					placeId: customSuburb.placeId,
				},
			});
		});

		customSuburbsDataLayer.revertStyle();
		customSuburbsDataLayer.setStyle((feature) => {
			// const feature = options.feature as google.maps.PlaceFeature;
			const placeId = feature?.getProperty('placeId') as string;

			return getStyleForSuburb(placeId);
		});

		customSuburbsDataLayer.addListener('click', (event: google.maps.MapMouseEvent) => {
			if ('feature' in event && event.feature) {
				//@ts-expect-error runtime check
				const placeId = event?.feature?.getProperty('placeId');

				if (placeId) {
					onSuburbSelected(placeId);
				}
			}
		});

		// Style the administrative areas (LGAs) with borders and subtle hover effects
		const councilStyleOptions: google.maps.FeatureStyleOptions = {
			strokeColor: '#000',
			fillColor: '#FFF',
			strokeOpacity: 1.0,
			strokeWeight: 8.0,
			fillOpacity: 0.8,
		};

		councilLayer.style = (options: google.maps.FeatureStyleFunctionOptions) => {
			const feature = options.feature as google.maps.PlaceFeature;

			if (feature.placeId === councilPlaceId) {
				return councilStyleOptions;
			}

			return {
				strokeColor: '#AAA',
				fillColor: '#AAA',
				strokeOpacity: 0.0,
				strokeWeight: 0.0,
				fillOpacity: 0.7,
			};
		};

		localityLayer.addListener('click', (event: google.maps.MapMouseEvent) => {
			if ('features' in event && Array.isArray(event.features) && event.features[0]?.placeId) {
				onSuburbSelected(event.features[0].placeId);
			}
		});

		localityLayer.style = (options: google.maps.FeatureStyleFunctionOptions) => {
			const feature = options.feature as google.maps.PlaceFeature;
			return getStyleForSuburb(feature.placeId);
		};

		// Cleanup function
		return () => {
			customSuburbsDataLayer.setMap(null);
		};
	}, [
		map,
		placesLib,
		activeViewport,
		councilPlaceId,
		suburbPlaceIds,
		onSuburbSelected,
		selectedSuburbPlaceId,
		oneFeatureIsSelected,
		heatOfPlaces,
		customSuburbs,
		getStyleForSuburb,
	]);

	return null;
};
